import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { admin } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import type { Team } from '../../types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { ErrorMessage, Field, Label } from '../../ui/fieldset';
import { Input } from '../../ui/input';
import { Text } from '../../ui/text';
import { EmptyState, PageHeader, Panel, StatCard } from '../../components/PageChrome';

export default function Teams() {
  const { groups } = useAuth();
  const isAdmin = groups.includes('admin');

  const [teams, setTeams] = useState<Team[]>([]);
  const [error, setError] = useState<string>();
  const [showNew, setShowNew] = useState(false);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [season, setSeason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await admin.listTeams();
      setTeams(res.teams);
    } catch (err) {
      setError(String(err));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await admin.createTeam({
        name: name.trim(),
        slug: slug.trim() || undefined,
        season: season.trim() || undefined,
      });
      setName('');
      setSlug('');
      setSeason('');
      setShowNew(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Platform"
        title="Teams"
        description="Create teams, check camera setup, and jump into roster or game-day tools."
        actions={
          isAdmin ? (
            <Button color="dark/zinc" onClick={() => setShowNew((v) => !v)}>
              {showNew ? 'Cancel' : 'New team'}
            </Button>
          ) : null
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Total teams" value={teams.length} />
        <StatCard label="Active" value={teams.filter((t) => t.active).length} tone="emerald" />
        <StatCard
          label="Camera ready"
          value={teams.filter((t) => t.channelArn).length}
          hint="Teams ready to go live"
        />
      </div>

      {showNew ? (
        <Panel>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="font-medium text-zinc-100">New team</div>
              <Text className="mt-1 text-sm">
                Start with team identity. Roster, schedule, fans, and channel setup follow inside the workspace.
              </Text>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field>
                <Label>Team name</Label>
                <Input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., RA Legends 11u"
                />
              </Field>
              <Field>
                <Label>Slug</Label>
                <Input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="optional, lowercase-with-dashes"
                />
              </Field>
              <Field>
                <Label>Season</Label>
                <Input
                  value={season}
                  onChange={(e) => setSeason(e.target.value)}
                  placeholder="e.g., 2026 Spring"
                />
              </Field>
            </div>
            {error ? <ErrorMessage>{error}</ErrorMessage> : null}
            <Button type="submit" color="dark/zinc" disabled={busy}>
              {busy ? 'Creating…' : 'Create team'}
            </Button>
          </form>
        </Panel>
      ) : null}

      {error && !showNew ? <Text className="text-red-400">{error}</Text> : null}

      {teams.length === 0 ? (
        <EmptyState
          title="No teams yet"
          body="Create your first team to unlock roster management, game scheduling, fan approvals, and streaming setup."
          action={
            isAdmin ? (
              <Button color="dark/zinc" onClick={() => setShowNew(true)}>
                New team
              </Button>
            ) : null
          }
        />
      ) : (
        <ul className="grid gap-3 lg:grid-cols-2">
          {teams.map((t) => (
            <li key={t.teamId}>
              <Link
                to={`/admin/teams/${t.teamId}`}
                className="block rounded-lg border border-zinc-800 bg-zinc-900 p-4 transition hover:border-zinc-700"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="font-medium text-zinc-100">{t.name}</span>
                    <Text className="mt-1 font-mono text-xs text-zinc-500">{t.teamId}</Text>
                  </div>
                  <div className="flex shrink-0 flex-wrap justify-end gap-2">
                    <Badge color={t.active ? 'lime' : 'zinc'}>
                      {t.active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge color={t.channelArn ? 'emerald' : 'amber'}>
                      {t.channelArn ? 'Channel' : 'No channel'}
                    </Badge>
                  </div>
                </div>
                <Text className="mt-3 text-sm">{t.season ?? 'No season set'}</Text>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
